import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AuthState = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: string[];
  isAdmin: boolean;
  isTherapist: boolean;
  displayName: string;
  roleLoading: boolean;
};


/**
 * Fuente unica de verdad de sesion y rol en el frontend.
 *
 * Los roles se resuelven con una sola llamada al RPC `my_roles`, que esta
 * anclado al usuario de la sesion y devuelve un array de textos, por ejemplo
 * ["admin","terapeuta","cliente"]. Si la llamada falla se asume ["cliente"].
 */
export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    const nameFromMetadata = (u: User | null | undefined) =>
      (u?.user_metadata?.nombre as string | undefined)
      || (u?.user_metadata?.display_name as string | undefined)
      || (u?.email ? u.email.split("@")[0] : "")
      || "";

    const loadRoleAndName = async (user: User) => {
      setRoleLoading(true);

      const { data: rolesData, error: rolesError } = await supabase.rpc("my_roles");
      if (rolesError) {
        console.warn("No se pudieron cargar los roles del usuario:", rolesError.message);
        setRoles(["cliente"]);
      } else {
        setRoles(rolesData && rolesData.length > 0 ? rolesData : ["cliente"]);
      }

      const esTerapeuta = Boolean(rolesData?.includes("terapeuta"));

      if (esTerapeuta) {
        const { data: therapist } = await supabase
          .from("profiles")
          .select("nombre")
          .eq("user_id", user.id)
          .maybeSingle();
        setDisplayName(therapist?.nombre || nameFromMetadata(user));
        setRoleLoading(false);
        return;
      }

      const { data: clientProfile } = await supabase
        .from("user_profiles")
        .select("display_name")
        .eq("user_id", user.id)
        .maybeSingle();

      setDisplayName(clientProfile?.display_name || nameFromMetadata(user));
      setRoleLoading(false);
    };

    const reset = () => {
      setRoles([]);
      setDisplayName("");
      setRoleLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
      if (nextSession?.user) {
        setDisplayName(nameFromMetadata(nextSession.user));
        // Diferido para no bloquear el callback de auth de Supabase
        setTimeout(() => { void loadRoleAndName(nextSession.user); }, 0);
      } else {
        reset();
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      if (data.session?.user) {
        setDisplayName(nameFromMetadata(data.session.user));
        void loadRoleAndName(data.session.user);
      } else {
        reset();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    user: session?.user ?? null,
    session,
    loading,
    roles,
    isAdmin: roles.includes("admin"),
    isTherapist: roles.includes("terapeuta"),
    displayName,
    roleLoading,
  };

}
